// @flow
import * as React from 'react'
import moment from 'moment'
import { FormattedMessage } from 'mz-intl'
import type { Flight } from 'mz-sdk/services/flights/getFlights'
import NoFlightsFoundMessage from '../NoFlightsFoundMessage'
import OptionItem from '../../../core/Dropdown/OptionItem'
import Form from '../../Form'
import TextField from '../../TextField/field'
import Button from '../../../core/Button'
import AutofocusElement from '../../../core/AutofocusElement'
import parseFlightQuery from '../utils/parseFlightQuery'
import flightToString from '../utils/flightToString'
import {
  Container,
  ButtonContainer,
  Header,
  StillCantFindNotice,
} from './elements'
import messages from '../messages.intl'

// Types
import type { FlightInfo } from '../BasicFlightField'

export type Props = {
  flightInfo: FlightInfo,
  airlineCode: string,
  airlineName: string,
  exactTimeFlights: Flight[],
  closeTimeFlights: Flight[],
  onSelectFlight: (Flight) => any,
  searchFlightDatetime: string,
}
type State = {
  showFligtNumberField: boolean,
}

// Utils
function validateFlightNumber(val) {
  const flightData = val && parseFlightQuery(val)
  if (flightData && flightData.flightNumber) return null
  return messages.INCORRECT_FLIGHT_NUMBER
}

class FlightSearchResult extends React.Component<Props, State> {
  state = {
    showFligtNumberField: false,
  }

  handleSelectFlight = (flight: Flight) => {
    const { onSelectFlight } = this.props
    onSelectFlight && onSelectFlight(flight)
  }

  handleSelectNonexistingFlight = (vals: Object) => {
    const { searchFlightDatetime, airlineName, airlineCode } = this.props
    const { airportCode, flightDirection } = this.props.flightInfo

    const flightData = parseFlightQuery(vals.flightNumber)
    const parsedPickupDatetime = moment.parseZone(searchFlightDatetime)
    const flightTime = parsedPickupDatetime.format('hh:mmA')
    const sources =
      flightDirection === 'dep'
        ? { origin: airportCode, destination: '' }
        : { origin: '', destination: airportCode }

    this.handleSelectFlight({
      ...sources,
      flightNumber: flightData?.flightNumber || '',
      flightCode: `${airlineCode}${flightData?.flightNumber || ''}`,
      airlineCode,
      airlineName,
      time: flightTime,
      flightDatetime: searchFlightDatetime,
    })
  }

  toggleFlightNumberField = () => {
    this.setState({ showFligtNumberField: true })
  }

  renderBestMatch() {
    if (!this.props.exactTimeFlights.length) return null
    return (
      <div>
        <Header>
          <FormattedMessage message={messages.BEST_MATCH} />
        </Header>
        {this.props.exactTimeFlights.map((x, i) => (
          <OptionItem
            key={i}
            value={x}
            renderOption={flightToString}
            onClick={this.handleSelectFlight}
          />
        ))}
      </div>
    )
  }

  renderPosibleOptions() {
    if (!this.props.closeTimeFlights.length) return null
    return (
      <div>
        <Header>
          <FormattedMessage message={messages.POSSIBLE_OPTIONS} />
        </Header>
        {this.props.closeTimeFlights.map((x, i) => (
          <OptionItem
            key={i}
            value={x}
            renderOption={flightToString}
            onClick={this.handleSelectFlight}
          />
        ))}
      </div>
    )
  }

  renderCustomSelector() {
    const {
      closeTimeFlights,
      exactTimeFlights,
      flightInfo,
      searchFlightDatetime,
    } = this.props
    const isResultsEmpty = !closeTimeFlights.length && !exactTimeFlights.length
    const headerMessage = isResultsEmpty ? (
      <NoFlightsFoundMessage
        flightInfo={{
          ...flightInfo,
          flightDatetime: searchFlightDatetime,
        }}
      />
    ) : (
      <FormattedMessage message={messages.STILL_CANT_FIND} />
    )

    return (
      <div>
        <Header>{headerMessage}</Header>
        <StillCantFindNotice>
          <FormattedMessage message={messages.STILL_CANT_FIND_NOTICE} />
        </StillCantFindNotice>
        {!this.state.showFligtNumberField && (
          <Button appearance="link" onClick={this.toggleFlightNumberField}>
            <FormattedMessage message={messages.ENTER_FLIGHT_MANUALLY} />
          </Button>
        )}
        {this.state.showFligtNumberField && this.renderFlightNumberForm()}
      </div>
    )
  }

  renderFlightNumberForm() {
    const { airlineCode } = this.props
    return (
      <Form
        onSubmit={this.handleSelectNonexistingFlight}
        render={({ submit }, nest) => (
          <div>
            <AutofocusElement
              render={({ setFocusableRef }) => (
                <TextField
                  {...nest('flightNumber')}
                  innerRef={setFocusableRef}
                  validate={validateFlightNumber}
                  label={<FormattedMessage message={messages.FIELD_LABEL} />}
                  placeholder={
                    <FormattedMessage
                      message={messages.FIELD_PLACEHOLER}
                      values={{ airlineCode }}
                    />
                  }
                />
              )}
            />
            <ButtonContainer>
              <Button onClick={submit}>
                <FormattedMessage message={messages.SELECT_ENTERED_FLIGHT} />
              </Button>
            </ButtonContainer>
          </div>
        )}
      />
    )
  }

  render() {
    return (
      <Container>
        {this.renderBestMatch()}
        {this.renderPosibleOptions()}
        {this.renderCustomSelector()}
      </Container>
    )
  }
}

export default FlightSearchResult
